import type { Page, Store } from '@/types/store';
import type {
    FunnelAnalytics,
    FunnelAnalyticsMap,
    FunnelListItem,
    FunnelPageData,
    FunnelPageItem,
    FunnelPageRouting,
    FunnelRouteTarget,
} from '../types';
import { genFunnelURL, getFunnelDisplayName } from './funnelDisplay';

/**
 * Transform a Store (funnel) into a FunnelListItem for display
 */
export function toFunnelListItem(store: Store, username: string, analyticsMap: FunnelAnalyticsMap): FunnelListItem {
    return {
        store_id: store.store_id,
        slug: store.slug,
        name: getFunnelDisplayName(store),
        firstPageType: store.first_page_type ?? null,
        pageCount: store.pages?.length ?? 0,
        analytics: analyticsMap[String(store.store_id)] ?? null,
        url: genFunnelURL(store.slug, username, false),
        noHttpsUrl: genFunnelURL(store.slug, username, true),
        pages: store.pages ?? [],
    };
}

/**
 * Transform a Page into a FunnelPageItem for the funnel detail view
 */
export function toFunnelPageItem(page: Page): FunnelPageItem {
    const data = page.data as FunnelPageData | undefined;
    const product = data?.product;
    const funnelPage = product?.funnel_page;
    const topBannerText = typeof data?.top_banner_text === 'string' ? data.top_banner_text : undefined;
    const funnelPrice = typeof product?.funnel_price === 'number' ? product.funnel_price : null;
    const funnelCtaText = typeof product?.funnel_cta_text === 'string' ? product.funnel_cta_text : '';
    const routing = parseRouting(data?.routing);

    return {
        page_id: page.page_id,
        title: product?.title || 'Untitled Page',
        type: page.type,
        status: page.status,
        order: page.order,
        rawOrder: page.order,
        image: product?.image,
        price: product?.price?.amount,
        currency: product?.price?.currency,
        description: product?.description,
        referencePageId: funnelPage?.reference_page_id,
        topBannerText,
        funnelPrice,
        funnelCtaText,
        routing,
    };
}

export function parseRoute(raw: unknown): FunnelRouteTarget | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const pageId = typeof r.page_id === 'number' ? r.page_id : null;
    if (pageId === null) return null;
    const label = typeof r.label === 'string' ? r.label : undefined;
    return label !== undefined ? { page_id: pageId, label } : { page_id: pageId };
}

export function parseRouting(raw: unknown): FunnelPageRouting | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const onPurchase = parseRoute(r.on_purchase);
    const onSkip = parseRoute(r.on_skip);
    if (!onPurchase && !onSkip) return null;
    return { on_purchase: onPurchase, on_skip: onSkip };
}

/**
 * Format analytics value with null safety
 */
export function getAnalyticsValue(analytics: FunnelAnalytics | null, key: keyof FunnelAnalytics): number {
    return analytics?.[key] ?? 0;
}
